import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Globe, Info, Loader2 } from 'lucide-react'
import { useResource, useUpdateResource } from '@/shared/hooks/useResources'
import { useTelegram } from '@/shared/hooks/useTelegram'

const COUNTRIES = [
  { code: 'RU', name: 'Россия', flag: '🇷🇺' },
  { code: 'UA', name: 'Украина', flag: '🇺🇦' },
  { code: 'BY', name: 'Беларусь', flag: '🇧🇾' },
  { code: 'KZ', name: 'Казахстан', flag: '🇰🇿' },
  { code: 'UZ', name: 'Узбекистан', flag: '🇺🇿' },
  { code: 'KG', name: 'Кыргызстан', flag: '🇰🇬' },
  { code: 'TJ', name: 'Таджикистан', flag: '🇹🇯' },
  { code: 'TM', name: 'Туркменистан', flag: '🇹🇲' },
  { code: 'AZ', name: 'Азербайджан', flag: '🇦🇿' },
  { code: 'AM', name: 'Армения', flag: '🇦🇲' },
  { code: 'GE', name: 'Грузия', flag: '🇬🇪' },
  { code: 'MD', name: 'Молдова', flag: '🇲🇩' },
]

export default function ResourceGeo() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const navigate = useNavigate()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading, showBackButton, hideBackButton } = useTelegram()
  const { data: resource, isLoading } = useResource(resourceId)
  const updateResource = useUpdateResource()

  const [selectedCountries, setSelectedCountries] = useState<string[]>([])
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<string>('')

  const handleSaveRef = useRef<() => Promise<void>>()

  // Initialize from resource data
  useEffect(() => {
    if (resource && !isInitialized) {
      setSelectedCountries(resource.geo_targeting?.countries || [])
      setIsInitialized(true)
    }
  }, [resource, isInitialized])

  const toggleCountry = (code: string) => {
    setSelectedCountries((prev) =>
      prev.includes(code)
        ? prev.filter((c) => c !== code)
        : [...prev, code]
    )
  }

  const toggleAll = () => {
    if (selectedCountries.length === COUNTRIES.length) {
      setSelectedCountries([])
    } else {
      setSelectedCountries(COUNTRIES.map((c) => c.code))
    }
  }

  const allSelected = selectedCountries.length === COUNTRIES.length
  const someExcluded = selectedCountries.length > 0 && selectedCountries.length < COUNTRIES.length

  const handleSave = async () => {
    if (!resource) return

    setError('')
    hapticFeedback('medium')
    setMainButtonLoading(true)

    try {
      await updateResource.mutateAsync({
        resourceId: resource.id,
        data: {
          geo_targeting: {
            countries: selectedCountries,
          },
        },
      })
      hapticNotification('success')

      setMainButtonLoading(false)
      hideMainButton()
      hideBackButton()
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`, {
        replace: true,
        state: { successMessage: 'Ресурс успешно обновлен' }
      })
    } catch (error) {
      console.error('Failed to update geo targeting:', error)
      setError('Ошибка при сохранении гео-таргетинга')
      hapticNotification('error')
      setMainButtonLoading(false)
    }
  }

  handleSaveRef.current = handleSave

  useEffect(() => {
    if (!isReady) return

    window.scrollTo(0, 0)

    const onSave = () => {
      handleSaveRef.current?.()
    }

    setMainButtonLoading(false)
    showMainButton('Сохранить гео', onSave, '#5288c1', '#ffffff')
    showBackButton(() => {
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`)
    })

    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideMainButton()
      hideBackButton()
      document.body.style.backgroundColor = ''
    }
  }, [isReady, showMainButton, hideMainButton, showBackButton, hideBackButton, setMainButtonLoading, navigate, resourceId])

  if (isLoading || !resource) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
              <Globe className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Гео-таргетинг</h1>
              <p className="text-sm text-tg-hint mt-1">{resource.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Toggle All */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4">
            <button
              onClick={() => {
                hapticFeedback('light')
                toggleAll()
              }}
              className="w-full flex items-center justify-between p-4 bg-tg-bg rounded-xl hover:bg-tg-hint/5 transition-colors"
            >
              <div className="text-left">
                <div className="font-medium text-tg-text">Все страны</div>
                <div className="text-xs text-tg-hint mt-0.5">
                  {allSelected ? 'Отменить выбор всех' : 'Выбрать все страны'}
                </div>
              </div>
              <div className={`relative w-12 h-7 rounded-full transition-colors ${
                allSelected ? 'bg-[#059669]' : 'bg-tg-hint/30'
              }`}>
                <div
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    allSelected ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </div>
            </button>
          </div>
        </div>

        {/* Country Grid */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <h2 className="font-semibold text-tg-text">Выберите страны</h2>
            <p className="text-xs text-tg-hint mt-1">
              Выбрано: {selectedCountries.length} из {COUNTRIES.length}
            </p>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-2 gap-2">
              {COUNTRIES.map((country) => {
                const isSelected = selectedCountries.includes(country.code)
                return (
                  <button
                    key={country.code}
                    onClick={() => {
                      hapticFeedback('light')
                      toggleCountry(country.code)
                    }}
                    className={`p-3 rounded-xl border-2 transition-all text-left ${
                      isSelected
                        ? 'border-[#5288c1] bg-[#5288c1]/10'
                        : 'border-transparent bg-tg-bg hover:border-tg-hint/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{country.flag}</span>
                      <div className="flex-1">
                        <div className={`text-sm font-medium ${
                          isSelected ? 'text-[#5288c1]' : 'text-tg-text'
                        }`}>
                          {country.name}
                        </div>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Warning for excluded countries */}
        {someExcluded && (
          <div className="p-3 bg-orange-600/10 border-2 border-orange-600/20 rounded-xl">
            <p className="text-sm text-orange-400 text-center font-medium">
              ⚠️ Пользователи из невыбранных стран не будут видеть ваш ресурс
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-600 text-center">{error}</p>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-tg-secondary-bg rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-tg-button flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-tg-text mb-1.5">О гео-таргетинге</h3>
              <p className="text-sm text-tg-hint leading-relaxed">
                Здесь будет подробное описание.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
